import React, { useCallback, useEffect, useImperativeHandle, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useForm } from "react-hook-form";
import CustomFormTextInput from "../../Components/Inputs/CustomFormTextInput";
import DialogModal from "../../Components/DialogModel";
import { postApi, deleteApi, getApi, putApi } from "../../../utils/services";
import { errorToast, successToast } from "../../../utils/toastHelper";
import usePermission from "../../../utils/customHooks/usePermission";
import { R_PROJECTS, R_PROJECTS_PROFILE } from "../../../utils/permissionReferenceConfig";
import { subModPermissionSelector } from "../../store/features/permissions/permissionsSlice";
import { GET_WORK_AREAS, ADD_WORK_AREA, UPDATE_WORK_AREA, DELETE_WORK_AREA, ADD_WORK_AREA_INVERTER,UPDATE_WORK_AREA_INVERTER, DELETE_WORK_AREA_INVERTER } from "../../../utils/services/apiPath";
import "./InverterDetailTable.scss";
import { prepareInitialConfig, changeMode } from "../../../utils/formHelper";

export const InverterDetailConfig = {
  workArea: {
    name: "workArea",
    label: "Work Area",
    perKey: "WorkAreaName",
    withInfoIcon: false,
    infoMessage: "",
    rules: {
      required: "Work Area is required!",
      maxLength: {
        value: 25,
        message: "Maximum 25 characters allowed"
      },
      pattern: {
        value: /^[A-Za-z0-9.-]+$/,
        message: "Work Area must contain only letters, numbers, dashes (-) or dots (.)"
      }
    }
  },

  description: {
    name: "description",
    label: "Description",
    perKey: "WorkAreaDescription",
    withInfoIcon: false,
    infoMessage: "",
    rules: {
      maxLength: {
        value: 100,
        message: "Maximum 100 characters allowed"
      }
    }
  },

  actions: {
    name: "actions",
    label: "Actions",
    perKey: "Actions"
  }
};

export const InverterConfig = {
  inverterName: {
    name: "inverterName",
    label: "Inverter Name",
    perKey: "WorkAreaInverterName",
    withInfoIcon: false,
    infoMessage: "",
    rules: {
      required: "Inverter Name is required!",
      maxLength: {
        value: 25,
        message: "Maximum 25 characters allowed"
      },
      pattern: {
        value: /^[A-Za-z0-9.-]+$/,
        message: "Inverter Name must contain only letters, numbers, dashes (-) or dots (.)"
      }
    }
  },

  description: {
    name: "description",
    label: "Description",
    perKey: "WorkAreaInverterDescription",
    withInfoIcon: false,
    infoMessage: "",
    rules: {
      maxLength: {
        value: 200,
        message: "Maximum 200 characters allowed"
      }
    }
  },

  actions: {
    name: "actions",
    label: "Actions",
    perKey: "Actions"
  }
};

export const inverterDetailFormNames = Object.keys(InverterConfig);

const FORM_FIELD_NAMES = ["workArea", "description"];
const INVERTER_FORM_FIELD_NAMES = ["inverterName", "description"];

const toTableColumns = (config) => Object.entries(config).map(([key, { label, perKey }]) => ({ key, label, perKey }));

const TABLE_COLUMNS = toTableColumns(InverterDetailConfig);
const INVERTER_COLUMNS = toTableColumns(InverterConfig);

const extractValueFromResponse = (rawResponse, key) => {
  const data = rawResponse?.data;
  return (
    data?.[key] ??
    data?.request?.[key] ??
    data?.result?.[key] ??
    (Array.isArray(data) ? data?.[0]?.[key] : undefined) ??
    data?.data?.[key] ??
    rawResponse?.[key] ??
    (Array.isArray(rawResponse) ? rawResponse?.[0]?.[key] : undefined)
  );
};

const extractErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  const responseData = error?.response?.data;

  if (responseData) {
    if (typeof responseData === "string") return responseData;
    if (responseData.message) return responseData.message;
    if (responseData.title) return responseData.title;

    const firstFieldError = Object.values(responseData).find(
      (value) => Array.isArray(value) && value.length > 0 && typeof value[0] === "string"
    );
    if (firstFieldError) return firstFieldError[0];
  }

  return error?.message || fallback;
};

const TimesheetInfoIcon = ({ message, size }) => (
  <Tooltip title={message} placement="top" arrow enterDelay={0} leaveDelay={0}>
    <button className={`work-areas-table__action-icon-btn work-areas-table__action-icon-btn--info${
      size ? " work-areas-table__action-icon-btn--info-lg" : "" }`}
      disabled={false}
      aria-label="Information about timesheet constraint"
    >
      <InfoOutlinedIcon />
    </button>
  </Tooltip>
);

const AddEntityPrompt = ({ article, entityLabel, onClick, disabled, title }) => (
  <div className="work-areas-table__empty-state-content">
    <div className="work-areas-table__empty-state-message">
      <span className="work-areas-table__empty-state-text">{article}</span>
      <button onClick={onClick} disabled={disabled} className="work-areas-table__empty-state-link" title={title}>
        {entityLabel}
      </button>
      <span className="work-areas-table__empty-state-text">for this Project</span>
    </div>
  </div>
);

const InverterDetailTable = React.forwardRef(function InverterDetailTable({projectId, isCreator = false}, ref) {
  const getFieldPermission = (fieldName) => {
    return workAreasPermission.fieldPerObj?.[fieldName] || workAreasPermission;
  };

  const hasPermission = (fieldName, action = "canView") => {
    const permission = getFieldPermission(fieldName);
    return permission[action] !== false;
  };

  const { trigger, getValues, setValue, clearErrors, control } = useForm({defaultValues: { workArea: "", description: "" }});

  const {
    trigger: inverterTrigger,
    getValues: inverterGetValues,
    setValue: inverterSetValue,
    clearErrors: inverterClearErrors,
    control: inverterControl
  } = useForm({ defaultValues: { inverterName: "", description: ""}
  });

  const [formConfig, setFormConfig] = useState(
    prepareInitialConfig(InverterDetailConfig)
  );

  const [inverterFormConfig, setInverterFormConfig] = useState(
    prepareInitialConfig(InverterConfig)
  );

  const [tableRows, setTableRows] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentEditingRowId, setCurrentEditingRowId] = useState(null);
  const [isInverterFormOpen, setIsInverterFormOpen] = useState(false);
  const [currentEditingInverterId, setCurrentEditingInverterId] = useState(null);
  const [currentInverterWorkAreaId, setCurrentInverterWorkAreaId] = useState(null);
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
  const [pendingDeleteRowId, setPendingDeleteRowId] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteTitle, setDeleteTitle] = useState("");
  const [pendingDeleteInverterId, setPendingDeleteInverterId] = useState(null);
  const [pendingDeleteWorkAreaIdForInverter, setPendingDeleteWorkAreaIdForInverter] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actionData, setActionData] = useState({});
  const [inverterActionData, setInverterActionData] = useState({});
  const [expandedWorkAreaId, setExpandedWorkAreaId] = useState(null);

  const { "Project Site: Work Areas": workAreasPermission } = usePermission( subModPermissionSelector(R_PROJECTS, R_PROJECTS_PROFILE), ["Project Site: Work Areas"]);

  if (!workAreasPermission?.canView) {
    return null;
  }

  const defaultFormProps = {control, formValues: actionData};

  const canDeleteRow = useCallback((rowData) => {
    return !rowData.hasTimesheetEntries && workAreasPermission.canDelete;
  }, [workAreasPermission.canDelete]);

  const isNewProject = !projectId || projectId === "";

  const toggleWorkAreaExpand = useCallback((workAreaId) => {
    setExpandedWorkAreaId(expandedWorkAreaId === workAreaId ? null : workAreaId);
  }, [expandedWorkAreaId]);

  const updateWorkAreaRow = useCallback((id, changes) => {
    setTableRows(previousRows =>
      previousRows.map(row => (row.id === id ? { ...row, ...changes } : row))
    );
  }, []);

  const addWorkAreaRow = useCallback((newRow) => {
    setTableRows(previousRows => [...previousRows, newRow]);
  }, []);

  const removeWorkAreaRow = useCallback((id) => {
    setTableRows(previousRows => previousRows.filter(row => row.id !== id));
  }, []);

  const updateInverterInRow = useCallback((workAreaId, inverterId, changes) => {
    setTableRows(previousRows =>
      previousRows.map(row =>
        row.id === workAreaId ? {
              ...row,
              inverters: row.inverters.map(inv =>
                inv.inverterGuid === inverterId ? { ...inv, ...changes } : inv
              )
            } : row
      )
    );
  }, []);

  const addInverterToRow = useCallback((workAreaId, newInverter) => {
    setTableRows(previousRows =>
      previousRows.map(row =>
        row.id === workAreaId ? { ...row, inverters: [...(row.inverters || []), newInverter] } : row
      )
    );
  }, []);

  const removeInverterFromRow = useCallback((workAreaId, inverterId) => {
    setTableRows(previousRows =>
      previousRows.map(row =>
        row.id === workAreaId ? { ...row, inverters: row.inverters.filter(inv => inv.inverterGuid !== inverterId) } : row
      )
    );
  }, []);

  const loadWorkAreas = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getApi(GET_WORK_AREAS(projectId));

      if (Array.isArray(response?.data)) {
        const mappedRows = response.data.map(item => ({
          id: item.workAreaGuid,
          workArea: item.workAreaName,
          workAreaName: item.workAreaName,
          description: item.description || "",
          hasTimesheetEntries: item.hasTimesheetEntries,
          inverters: item.inverters || []
        }));

        setTableRows(mappedRows);
      }
    } catch (error) {
      errorToast(extractErrorMessage(error, "Error loading work areas"));
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  const handleAddUpdateRow = useCallback(async (data) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const trimmedWorkArea = data.workArea.trim();
      const description = data.description || "";
      const payload = {
        workAreaName: trimmedWorkArea,
        description
      };

      const isDuplicate = tableRows.some(row =>
        row.id !== currentEditingRowId &&
        row.workArea.trim().toLowerCase() === trimmedWorkArea.toLowerCase()
      );

      if (isDuplicate) {
        errorToast(`${trimmedWorkArea} Work Area already exists for this project. Enter a unique Work Area.`);
        setIsSaving(false);
        return;
      }

      if (isNewProject) {
        if (currentEditingRowId) {
          updateWorkAreaRow(currentEditingRowId, { workArea: trimmedWorkArea, description });
        } else {
          addWorkAreaRow({
            id: `temp-${Date.now()}`,
            workArea: trimmedWorkArea,
            workAreaName: trimmedWorkArea,
            description,
            hasTimesheetEntries: false,
            inverters: []
          });
        }

        resetForm();
        return;
      }

      if (currentEditingRowId) {
        const rawResponse = await putApi(
          UPDATE_WORK_AREA(projectId, currentEditingRowId),
          payload
        );

        const { error } = rawResponse || {};

        if (error) {
          const errorMessage = extractErrorMessage(error, "Error updating work area");
          errorToast(errorMessage);
          setIsSaving(false);
          return;
        }

        updateWorkAreaRow(currentEditingRowId, { workArea: trimmedWorkArea, description });
        successToast(extractValueFromResponse(rawResponse, "message") || "Work Area Updated Successfully");
      } else {
        const rawResponse = await postApi(
          ADD_WORK_AREA(projectId),
          [payload]
        );

        const { error } = rawResponse || {};

        if (error) {
          const errorMessage = extractErrorMessage(error, "Error adding work area");
          errorToast(errorMessage);
          setIsSaving(false);
          return;
        }

        const newWorkAreaGuid = extractValueFromResponse(rawResponse, "workAreaGuid");

        addWorkAreaRow({
          id: newWorkAreaGuid,
          workArea: trimmedWorkArea,
          workAreaName: trimmedWorkArea,
          description,
          hasTimesheetEntries: extractValueFromResponse(rawResponse, "hasTimesheetEntries") ?? false,
          inverters: []
        });
        successToast(extractValueFromResponse(rawResponse, "message") || "Work Area Added Successfully");
      }

      resetForm();
    } catch (error) {
      errorToast(extractErrorMessage(error, "Error saving work area"));
    } finally {
      setIsSaving(false);
    }
  }, [projectId, currentEditingRowId, isSaving, isNewProject, tableRows, updateWorkAreaRow, addWorkAreaRow]);

  const handleDeleteRow = useCallback((rowData) => {
    if (!isNewProject && rowData.hasTimesheetEntries) {
      setDeleteTitle("Cannot Delete Work Area");
      setDeleteMessage("The work area has been used on a timesheet. It cannot be deleted.");
      setPendingDeleteRowId(null);
      setIsDeleteConfirmationDialogOpen(true);
      return;
    }

    const hasInverters = rowData.inverters && rowData.inverters.length > 0;
    setDeleteTitle("Delete Work Area");
    setDeleteMessage(
      hasInverters
        ? "Deleting this Work Area will also delete all the inverters inside it. Do you want to continue?"
        : "Are you sure you want to delete this record?"
    );
    setPendingDeleteRowId(rowData.id);
    setIsDeleteConfirmationDialogOpen(true);
  }, [isNewProject]);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteRowId) {
      setIsDeleteConfirmationDialogOpen(false);
      return;
    }

    setIsSaving(true);

    try {
      let deleteResponse;

      if (!isNewProject) {
        deleteResponse = await deleteApi(
          DELETE_WORK_AREA(projectId, pendingDeleteRowId)
        );

        const { error } = deleteResponse || {};

        if (error) {
          errorToast(extractErrorMessage(error, "Error deleting work area"));
          return;
        }
      }

      removeWorkAreaRow(pendingDeleteRowId);
      if (!isNewProject) {
        successToast(extractValueFromResponse(deleteResponse, "message") || "Work Area Deleted Successfully");
      }
    } catch (error) {
      errorToast(extractErrorMessage(error, "Error deleting work area"));
    } finally {
      setIsDeleteConfirmationDialogOpen(false);
      setPendingDeleteRowId(null);
      setDeleteMessage("");
      setDeleteTitle("");
      setIsSaving(false);
    }
  }, [projectId, pendingDeleteRowId, isNewProject, removeWorkAreaRow]);

  const handleDeleteInverter = useCallback((workAreaId, inverter) => {
    if (inverter.hasTimesheetEntries) {
      setDeleteTitle("Cannot Delete Inverter");
      setDeleteMessage("This inverter has been used on a timesheet. It cannot be deleted.");
      setIsDeleteConfirmationDialogOpen(true);
      setPendingDeleteInverterId(null);
      return;
    }

    setDeleteTitle("Delete Inverter");
    setDeleteMessage("Are you sure you want to delete this record?");
    setPendingDeleteInverterId(inverter.inverterGuid);
    setPendingDeleteWorkAreaIdForInverter(workAreaId);
    setIsDeleteConfirmationDialogOpen(true);
  }, []);

  const handleConfirmDeleteInverter = useCallback(async () => {
    if (!pendingDeleteInverterId || !pendingDeleteWorkAreaIdForInverter) {
      setIsDeleteConfirmationDialogOpen(false);
      return;
    }

    setIsSaving(true);

    try {
      let deleteResponse;

      if (!isNewProject) {
        deleteResponse = await deleteApi(
          DELETE_WORK_AREA_INVERTER(projectId, pendingDeleteWorkAreaIdForInverter, pendingDeleteInverterId)
        );

        const { error } = deleteResponse || {};

        if (error) {
          const errorMessage = extractErrorMessage(error, "Error deleting inverter");
          errorToast(errorMessage);
          return;
        }
      }

      removeInverterFromRow(pendingDeleteWorkAreaIdForInverter, pendingDeleteInverterId);
      if (!isNewProject) {
        successToast(extractValueFromResponse(deleteResponse, "message") || "Inverter Deleted Successfully");
      }
    } catch (error) {
      errorToast(extractErrorMessage(error, "Error deleting inverter"));
    } finally {
      setIsDeleteConfirmationDialogOpen(false);
      setPendingDeleteInverterId(null);
      setPendingDeleteWorkAreaIdForInverter(null);
      setDeleteMessage("");
      setDeleteTitle("");
      setIsSaving(false);
    }
  }, [pendingDeleteInverterId, pendingDeleteWorkAreaIdForInverter, projectId, isNewProject, removeInverterFromRow]);

  const resetForm = useCallback(() => {
    setActionData({});
    setCurrentEditingRowId(null);
    setFormConfig(changeMode());

    setValue("workArea", "");
    setValue("description", "");

    clearErrors(FORM_FIELD_NAMES);

    setIsFormOpen(false);
  }, [setValue, clearErrors]);

  const handleAddWorkAreaClick = useCallback(() => {
    setActionData({});
    setCurrentEditingRowId(null);
    setFormConfig(changeMode("edit"));

    setValue("workArea", "");
    setValue("description", "");

    clearErrors(FORM_FIELD_NAMES);
    setIsFormOpen(true);
  }, [setValue, clearErrors]);

  const handleEditRow = useCallback((rowData) => {
    if (!workAreasPermission.canEdit) return;

    if (!rowData.id) {
      errorToast("This work area's ID isn't available yet. Please refresh the page and try again.");
      return;
    }

    setActionData(rowData);
    setFormConfig(changeMode("edit"));
    setCurrentEditingRowId(rowData.id);
    setIsFormOpen(true);

    setValue("workArea", rowData.workArea);
    setValue("description", rowData.description || "");
  }, [workAreasPermission.canEdit, setValue]);

  const handleCancelForm = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleSaveForm = useCallback(async () => {
    if (isSaving) return;

    const isValidForm = await trigger(["workArea"]);
    if (!isValidForm) {
      return;
    }

    const formValues = getValues();
    await handleAddUpdateRow(formValues);
  }, [trigger, getValues, handleAddUpdateRow, isSaving]);

  const resetInverterForm = useCallback(() => {
    setInverterActionData({});
    setCurrentEditingInverterId(null);
    setCurrentInverterWorkAreaId(null);
    setInverterFormConfig(changeMode());

    inverterSetValue("inverterName", "");
    inverterSetValue("description", "");

    inverterClearErrors(INVERTER_FORM_FIELD_NAMES);

    setIsInverterFormOpen(false);
  }, [inverterSetValue, inverterClearErrors]);

  const handleAddInverterClick = useCallback((workAreaId) => {
    setCurrentInverterWorkAreaId(workAreaId);
    setInverterActionData({});
    setCurrentEditingInverterId(null);
    setInverterFormConfig(changeMode("edit"));

    inverterSetValue("inverterName", "");
    inverterSetValue("description", "");

    inverterClearErrors(INVERTER_FORM_FIELD_NAMES);
    setIsInverterFormOpen(true);

    setExpandedWorkAreaId(workAreaId);
  }, [inverterSetValue, inverterClearErrors]);

  const handleEditInverterClick = useCallback((workAreaId, inverter) => {
    const isPlaceholderId = inverter.inverterGuid?.toString().startsWith("inverter-");

    if (!isNewProject && isPlaceholderId) {
      errorToast("This inverter's ID isn't available yet. Please refresh the page and try again.");
      return;
    }

    setCurrentInverterWorkAreaId(workAreaId);
    setCurrentEditingInverterId(inverter.inverterGuid);
    setInverterActionData(inverter);
    setInverterFormConfig(changeMode("edit"));

    inverterSetValue("inverterName", inverter.inverterName);
    inverterSetValue("description", inverter.description || "");

    inverterClearErrors(INVERTER_FORM_FIELD_NAMES);
    setIsInverterFormOpen(true);
  }, [inverterSetValue, inverterClearErrors, isNewProject]);

  const handleCancelInverterForm = useCallback(() => {
    resetInverterForm();
  }, [resetInverterForm]);

  const handleSaveInverterForm = useCallback(async () => {
    if (isSaving) return;

    const isValidForm = await inverterTrigger(["inverterName"]);
    if (!isValidForm) {
      return;
    }

    setIsSaving(true);
    try {
      const formValues = inverterGetValues();
      const description = formValues.description || "";

      const inverterPayload = {
        inverterName: formValues.inverterName,
        description
      };

      if (isNewProject) {
        if (currentEditingInverterId) {
          updateInverterInRow(currentInverterWorkAreaId, currentEditingInverterId, {
            inverterName: formValues.inverterName,
            description
          });
        } else {
          addInverterToRow(currentInverterWorkAreaId, {
            inverterGuid: `inverter-temp-${Date.now()}`,
            inverterName: formValues.inverterName,
            description,
            hasTimesheetEntries: false
          });
        }

        resetInverterForm();
        return;
      }

      if (currentEditingInverterId) {
        const rawResponse = await putApi(
          UPDATE_WORK_AREA_INVERTER(projectId, currentInverterWorkAreaId, currentEditingInverterId),
          inverterPayload
        );

        const { error } = rawResponse || {};

        if (error) {
          const errorMessage = extractErrorMessage(error, "Error updating inverter");
          errorToast(errorMessage);
          setIsSaving(false);
          return;
        }

        updateInverterInRow(currentInverterWorkAreaId, currentEditingInverterId, {
          inverterName: formValues.inverterName,
          description
        });
        successToast(extractValueFromResponse(rawResponse, "message") || "Inverter Updated Successfully");
      } else {
        const rawResponse = await postApi(
          ADD_WORK_AREA_INVERTER(projectId, currentInverterWorkAreaId),
          inverterPayload
        );

        const { error } = rawResponse || {};

        if (error) {
          const errorMessage = extractErrorMessage(error, "Error adding inverter");
          errorToast(errorMessage);
          setIsSaving(false);
          return;
        }

        const inverterGuid = extractValueFromResponse(rawResponse, "inverterGuid");

        addInverterToRow(currentInverterWorkAreaId, {
          inverterGuid: inverterGuid || `inverter-${Date.now()}`,
          inverterName: formValues.inverterName,
          description,
          hasTimesheetEntries: extractValueFromResponse(rawResponse, "hasTimesheetEntries") ?? false
        });
        successToast(extractValueFromResponse(rawResponse, "message") || "Inverter Added Successfully");
      }

      resetInverterForm();
    } catch (error) {
      errorToast(extractErrorMessage(error, "Error saving inverter"));
    } finally {
      setIsSaving(false);
    }
  }, [
    inverterTrigger,
    inverterGetValues,
    currentInverterWorkAreaId,
    currentEditingInverterId,
    isSaving,
    projectId,
    isNewProject,
    updateInverterInRow,
    addInverterToRow
  ]);

  useEffect(() => {
    if (!isCreator && isInverterFormOpen && !currentEditingInverterId) {
      resetInverterForm();
    }
  }, [isCreator, isInverterFormOpen, currentEditingInverterId, resetInverterForm]);

  useEffect(() => {
    if (!isCreator && isFormOpen && !currentEditingRowId) {
      resetForm();
    }
  }, [isCreator, isFormOpen, currentEditingRowId, resetForm]);

  useEffect(() => {
    if (!projectId) return;
    loadWorkAreas();
  }, [projectId, loadWorkAreas]);

  useImperativeHandle(ref, () => ({
    getTableRows: () => tableRows,

    getUnsavedWorkAreas: () => {
      return tableRows
        .filter(row => !row.id || row.id.toString().startsWith("temp-"))
        .map(row => ({
          workAreaName: row.workArea,
          description: row.description || "",
          inverters: (row.inverters || [])
            .filter(inv => !inv.inverterGuid || inv.inverterGuid.toString().startsWith("inverter-temp-"))
            .map(inv => ({
              inverterName: inv.inverterName,
              description: inv.description || ""
            }))
        }));
    },

    loadWorkAreas: async () => {
      await loadWorkAreas();
    }
  }), [tableRows, loadWorkAreas]);

  return (
    <div className="work-areas-table-root">
      <div className="work-areas-table__toolbar">

        <button
          className="work-areas-table__toolbar-btn work-areas-table__toolbar-btn--primary"
          onClick={handleAddWorkAreaClick}
          disabled={
            isFormOpen ||
            isInverterFormOpen ||
            isSaving ||
            isLoading ||
            !hasPermission("WorkAreaName", "canAdd") ||
            !isCreator
          }
          title={
            !isCreator
              ? "You don't have permission to add work areas"
              : !hasPermission("WorkAreaName", "canAdd")
              ? "You don't have permission to add work areas"
              : isInverterFormOpen
              ? "Cannot add work area while editing inverter"
              : "Add a new work area"
          }
        >
          <AddIcon className="work-areas-table__toolbar-btn-icon" />
          Add Work Area
        </button>
      </div>

      <div className="work-areas-table__container">
        <table className="work-areas-table__table">
          <thead>
            <tr className="work-areas-table__head-row">
              {TABLE_COLUMNS.map(columnData => (
                <th key={columnData.key} className="work-areas-table__th">
                  {columnData.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {isFormOpen && (
              <tr className="work-areas-table__form-row">
                {TABLE_COLUMNS.map(columnData => (
                  <td key={columnData.key} className="work-areas-table__form-td">
                    {columnData.key === "actions" ? (
                      <div className="work-areas-table__form-actions">
                        <button
                          className="work-areas-table__cancel-btn"
                          onClick={handleCancelForm}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                        <button
                          className="work-areas-table__save-btn"
                          onClick={handleSaveForm}
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    ) : (
                      <div className="work-areas-table__form-cell">
                        {columnData.key === "workArea" && formConfig.workArea && (
                          <CustomFormTextInput
                            {...defaultFormProps}
                            {...formConfig.workArea}
                            permission={workAreasPermission.fieldPerObj?.[formConfig.workArea.perKey]}
                          />
                        )}

                        {columnData.key === "description" && formConfig.description && (
                          <CustomFormTextInput
                            {...defaultFormProps}
                            {...formConfig.description}
                            permission={workAreasPermission.fieldPerObj?.[formConfig.description.perKey]
                            }
                          />
                        )}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            )}

            {isLoading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="work-areas-table__loading-cell">
                  Loading work areas...
                </td>
              </tr>
            ) : !isFormOpen && tableRows.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="work-areas-table__empty-state">
                  <AddEntityPrompt
                    article="Add a"
                    entityLabel="Work Area"
                    onClick={handleAddWorkAreaClick}
                    disabled={isSaving || isLoading || !workAreasPermission.canEdit || !isCreator}
                    title={!isCreator ? "You don't have permission to add work areas" : "Add a new work area"}
                  />
                </td>
              </tr>
            ) : (
              tableRows.map(rowData => (
                <React.Fragment key={rowData.id}>
                  <tr className="work-areas-table__data-row work-areas-table__accordion-row">
                    {TABLE_COLUMNS.map(columnData => (
                      <td
                        key={columnData.key}
                        className={`work-areas-table__td${rowData[columnData.key] === "-" ? " work-areas-table__td--muted" : ""}`}
                      >
                        {columnData.key === "workArea" ? (
                          <div className="work-areas-table__accordion-header">
                            <button
                              className="work-areas-table__accordion-toggle"
                              onClick={() => toggleWorkAreaExpand(rowData.id)}
                              aria-expanded={expandedWorkAreaId === rowData.id}
                              aria-label={`Toggle ${rowData.workArea} details`}
                            >
                              {expandedWorkAreaId === rowData.id ? (
                                <ExpandMoreIcon className="work-areas-table__accordion-icon" />
                              ) : (
                                <ChevronRightIcon className="work-areas-table__accordion-icon" />
                              )}
                            </button>
                            <div className="work-areas-table__accordion-content">
                              <div className="work-areas-table__name-with-info">
                                <span className="work-areas-table__accordion-title">{rowData.workArea}</span>

                                {rowData.hasTimesheetEntries && (
                                  <TimesheetInfoIcon
                                    message="This Work Area cannot be edited because it has already been used in an uploaded timesheet. Add a new Work Area to proceed further."
                                    size="24px"
                                  />
                                )}
                              </div>

                              {rowData.inverters && rowData.inverters.length > 0 && (
                                <span className="work-areas-table__inverter-count">
                                  {rowData.inverters.length} inverter{rowData.inverters.length !== 1 ? "s" : ""}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : columnData.key === "actions" ? (
                          <div className="work-areas-table__actions">
                            <button
                              className="work-areas-table__action-btn-text"
                              onClick={() => handleAddInverterClick(rowData.id)}
                              disabled={isInverterFormOpen || isFormOpen || isLoading || !isCreator}
                              title={
                                !isCreator
                                  ? "You don't have permission to add inverters"
                                  : isFormOpen
                                  ? "Cannot add inverter while editing work area"
                                  : "Add a new inverter"
                              }
                            >
                              <PlaylistAddIcon className="work-areas-table__action-btn-icon" />
                              Add inverter
                            </button>

                            <button
                              className="work-areas-table__action-icon-btn"
                              title={
                                !isCreator
                                  ? "You don't have permission to edit work areas"
                                  : isInverterFormOpen
                                  ? "Cannot edit work area while editing inverter"
                                  : rowData.hasTimesheetEntries
                                  ? "This Work Area cannot be edited because it has already been used in an uploaded timesheet. Add a new Work Area to proceed further"
                                  : !hasPermission("WorkAreaName", "canEdit")
                                  ? "You don't have permission to edit work areas"
                                  : "Edit"
                              }
                              onClick={() => handleEditRow(rowData)}
                              disabled={isFormOpen || isInverterFormOpen || isSaving || rowData.hasTimesheetEntries || !hasPermission("WorkAreaName", "canEdit") || !isCreator}
                              aria-label={`Edit ${rowData.workArea}`}
                            >
                              <EditIcon />
                            </button>

                            <button
                              className={`work-areas-table__action-icon-btn work-areas-table__action-icon-btn--delete ${
                                rowData.hasTimesheetEntries ? "work-areas-table__action-icon-btn--disabled" : ""
                              }`}
                              title={
                                !isCreator
                                  ? "You don't have permission to delete work areas"
                                  : !hasPermission("WorkAreaName", "canDelete")
                                  ? "You don't have permission to delete work areas"
                                  : isInverterFormOpen
                                  ? "Cannot delete work area while editing inverter"
                                  : rowData.hasTimesheetEntries
                                  ? "Cannot delete - this work area has timesheet entries"
                                  : "Delete"
                              }
                              onClick={() => handleDeleteRow(rowData)}
                              disabled={isFormOpen || isInverterFormOpen || isSaving || !canDeleteRow(rowData) || !hasPermission("WorkAreaName", "canDelete") || !isCreator}
                              aria-label={`Delete ${rowData.workArea}`}
                            >
                              <DeleteIcon />
                            </button>
                          </div>
                        ) : (
                          rowData[columnData.key] || "—"
                        )}
                      </td>
                    ))}
                  </tr>

                  {expandedWorkAreaId === rowData.id && (
                    <tr className="work-areas-table__expanded-row">
                      <td colSpan={TABLE_COLUMNS.length}>
                        <div className="work-areas-table__inverters-section">
                          {isInverterFormOpen && currentInverterWorkAreaId === rowData.id && (
                            <table className="work-areas-table__table work-areas-table__inverters-form-table">
                              <tbody>
                                <tr className="work-areas-table__form-row">
                                  {INVERTER_COLUMNS.map(columnData => (
                                    <td key={columnData.key} className="work-areas-table__form-td">
                                      {columnData.key === "actions" ? (
                                        <div className="work-areas-table__form-actions">
                                          <button
                                            className="work-areas-table__cancel-btn"
                                            onClick={handleCancelInverterForm}
                                            disabled={isSaving}
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            className="work-areas-table__save-btn"
                                            onClick={handleSaveInverterForm}
                                            disabled={isSaving}
                                          >
                                            {isSaving ? "Saving..." : "Save"}
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="work-areas-table__form-cell">
                                          {columnData.key === "inverterName" && inverterFormConfig.inverterName && (
                                            <CustomFormTextInput
                                              control={inverterControl}
                                              formValues={inverterActionData}
                                              {...inverterFormConfig.inverterName}
                                              permission={workAreasPermission.fieldPerObj?.[inverterFormConfig.inverterName.perKey]}
                                            />
                                          )}

                                          {columnData.key === "description" && inverterFormConfig.description && (
                                            <CustomFormTextInput
                                              control={inverterControl}
                                              formValues={inverterActionData}
                                              {...inverterFormConfig.description}
                                              permission={workAreasPermission.fieldPerObj?.[inverterFormConfig.description.perKey]}
                                            />
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          )}

                          {!isInverterFormOpen && rowData.inverters && rowData.inverters.length === 0 && (
                            <div className="work-areas-table__inverters-empty-state">
                              <AddEntityPrompt
                                article="Add an"
                                entityLabel="Inverter"
                                onClick={() => handleAddInverterClick(rowData.id)}
                                disabled={isInverterFormOpen || isLoading || !isCreator}
                                title={!isCreator ? "You don't have permission to add inverters" : "Add a new inverter"}
                              />
                            </div>
                          )}

                          {rowData.inverters && rowData.inverters.length > 0 && (
                            <div className="work-areas-table__inverters-table-wrapper">
                              <table className="work-areas-table__table">
                                <tbody>
                                  {rowData.inverters.map(inverter => (
                                    <tr key={inverter.inverterGuid} className="work-areas-table__data-row">
                                      {INVERTER_COLUMNS.map(columnData => (
                                        <td key={columnData.key} className="work-areas-table__td">
                                          {columnData.key === "inverterName" ? (
                                            <div className="work-areas-table__name-with-info">
                                              <span>{inverter.inverterName}</span>

                                              {inverter.hasTimesheetEntries && (
                                                <TimesheetInfoIcon message="This Inverter cannot be edited because it has already been used in an uploaded timesheet. Add a new Inverter to proceed further." />
                                              )}
                                            </div>
                                          ) : columnData.key === "description" ? (
                                            <span>{inverter.description || "—"}</span>
                                          ) : columnData.key === "actions" ? (
                                            <div className="work-areas-table__actions">
                                              <button
                                                className="work-areas-table__action-icon-btn"
                                                title={
                                                  !isCreator
                                                    ? "You don't have permission to edit inverters"
                                                    : !hasPermission("WorkAreaInverterName", "canEdit")
                                                    ? "You don't have permission to edit inverters"
                                                    : isFormOpen
                                                    ? "Cannot edit inverter while editing work area"
                                                    : inverter.hasTimesheetEntries
                                                    ? "This Inverter cannot be edited because it has already been used in an uploaded timesheet. Add a new Inverter to proceed further"
                                                    : "Edit inverter"
                                                }
                                                onClick={() => handleEditInverterClick(rowData.id, inverter)}
                                                disabled={isFormOpen || inverter.hasTimesheetEntries || !hasPermission("WorkAreaInverterName", "canEdit") || !isCreator}
                                                aria-label={`Edit ${inverter.inverterName}`}
                                              >
                                                <EditIcon />
                                              </button>

                                              <button
                                                className={`work-areas-table__action-icon-btn work-areas-table__action-icon-btn--delete ${
                                                  inverter.hasTimesheetEntries ? "work-areas-table__action-icon-btn--disabled" : ""
                                                }`}
                                                title={
                                                  !isCreator
                                                    ? "You don't have permission to delete inverters"
                                                    : !hasPermission("WorkAreaInverterName", "canDelete")
                                                    ? "You don't have permission to delete inverters"
                                                    : isFormOpen
                                                    ? "Cannot delete inverter while editing work area"
                                                    : inverter.hasTimesheetEntries
                                                    ? "Cannot delete - this inverter has timesheet entries"
                                                    : "Delete inverter"
                                                }
                                                onClick={() => handleDeleteInverter(rowData.id, inverter)}
                                                disabled={isFormOpen || !hasPermission("WorkAreaInverterName", "canDelete") || !isCreator || inverter.hasTimesheetEntries}
                                                aria-label={`Delete ${inverter.inverterName}`}
                                              >
                                                <DeleteIcon />
                                              </button>
                                            </div>
                                          ) : (
                                            "—"
                                          )}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <DialogModal
        show={isDeleteConfirmationDialogOpen}
        title={deleteTitle}
        message={deleteMessage}
        confirmBtnText={ pendingDeleteRowId === null && pendingDeleteInverterId === null ? "OK" : "Yes" }
        cancelBtnText={ pendingDeleteRowId === null && pendingDeleteInverterId === null ? undefined : "No"}
        onConfirm={ pendingDeleteInverterId ? handleConfirmDeleteInverter : handleConfirmDelete }
        onCancel={() => {
          setIsDeleteConfirmationDialogOpen(false);
          setPendingDeleteRowId(null);
          setPendingDeleteInverterId(null);
          setPendingDeleteWorkAreaIdForInverter(null);
          setDeleteMessage("");
          setDeleteTitle("");
        }}
        showCancel={pendingDeleteRowId !== null || pendingDeleteInverterId !== null}
      />
    </div>
  );
});

InverterDetailTable.propTypes = {
  projectId: PropTypes.string.isRequired,
  isCreator: PropTypes.bool
};

InverterDetailTable.displayName = "InverterDetailTable";

export default InverterDetailTable;